// apps/frontend/src/lib/behaviorTracking.ts
// Tracking comportemental persist en DB (analytics_events).
// Spar de lib/analytics.ts (qui pousse vers Plausible)  on garde les deux,
// chacun pour son usage : Plausible = funnel/retention vue produit,
// behaviorTracking = events SQL-able pour dashboard demande custom.

import { API_BASE_URL } from '../config/api';
import { getSessionId } from './sessionId';

// ============================================================================
// UNION STRICTE DES EVENTS AUTORISS
// ============================================================================

// Toute typo ici = erreur compile. vite la pollution analytics ct prod.
export type BehaviorEventName =
  | 'page_view'
  | 'product_impression'
  | 'product_click'
  | 'favorite_click'
  | 'message_seller_click'
  | 'feed_scroll_depth'
  | 'product_shared'
  | 'shop_shared'
  | 'search_performed'
  // Funnel publication — 3 events critiques pour mesurer le flow vendeur
  | 'publication_started'
  | 'publication_completed'
  | 'publication_abandoned'
  // Qualité IA — 2 events "gratuits" car la data existe déjà côté modal,
  // signalent si la classification IA est fiable ou si la vendeuse corrige
  | 'ai_category_overridden'
  | 'ai_target_gender_overridden';

// Properties typs par event  refus d'envoyer un product_click sans productId.
type EventProperties = {
  // page_view : envoyé à chaque navigation SPA. La page elle-même est dans
  // le champ `page_url` de analytics_events ; ici les propriétés portent
  // juste le contexte navigation (referrer interne, durée page précédente).
  page_view: {
    referrer?: string;
    prevPageMs?: number;
  };
  product_impression: {
    productId: string;
    category?: string;
    sellerId?: string;
  };
  product_click: { productId: string; category?: string; sellerId?: string };
  favorite_click: {
    productId: string;
    action: 'add' | 'remove';
    category?: string;
    sellerId?: string;
  };
  message_seller_click: { productId: string; sellerId: string };
  feed_scroll_depth: { depth: number; category?: string };
  product_shared: { productId: string; platform: string; sellerId?: string };
  shop_shared: { shopOwnerId: string; platform: string };
  search_performed: { query: string; resultsCount: number };

  // ─── Funnel publication ────────────────────────────────────────────────
  // Source = depuis où la modal s'ouvre (URL au moment de l'open). Permet
  // de voir si les ouvertures depuis le dashboard convertissent mieux que
  // celles depuis le header par exemple.
  // publicationSessionId : uuid genere a l'ouverture, repropage dans
  // completed et abandoned. Permet de joindre 1-a-1 cote analyse pour
  // recuperer le categoryMain final d'un start (qui ne le connait pas
  // encore au moment du fire).
  publication_started: {
    source: string;
    publicationSessionId: string;
  };
  // Au succès : temps total, nb photos finales, catégorie principale,
  // présence d'une description. category_main est en or pour découper
  // les funnel par grande famille (toys vs clothing vs books...).
  publication_completed: {
    timeToPublishMs: number;
    photosCount: number;
    categoryMain: string | null;
    hasDescription: boolean;
    publicationSessionId: string;
  };
  // À l'abandon (fermeture avec input non vide) : où la vendeuse a quitté.
  // step = 'photos' tant qu'elle n'a pas ajouté de photo, 'details' sinon.
  // category_main peut être null si elle a abandonné avant de choisir.
  publication_abandoned: {
    step: 'photos' | 'details';
    timeOpenMs: number;
    photosCount: number;
    categoryMain: string | null;
    hasTitle: boolean;
    hasPrice: boolean;
    publicationSessionId: string;
  };

  // ─── Qualité IA ────────────────────────────────────────────────────────
  // Fire UNIQUEMENT si la vendeuse a effectivement publié AVEC une
  // catégorie différente de celle suggérée par l'IA. Sinon on capte du
  // bruit (changements intermédiaires sans valeur). Signal direct sur la
  // fiabilité de la suggestion IA depuis l'image / le titre.
  ai_category_overridden: {
    suggestedMain: string | null;
    finalMain: string | null;
    suggestedCategory: string | null;
    finalCategory: string | null;
  };
  // Même logique pour le picker "Pour qui ?" pré-rempli depuis la catégorie.
  // Si la vendeuse rectifie (ex: catégorie polyvalente pré-remplie FEMME
  // mais elle finit en HOMME), c'est un signal de re-flux user.
  ai_target_gender_overridden: {
    prefilled: string;
    final: string;
    categoryMain: string | null;
  };
};

// ============================================================================
// DDUPLICATION : viter les doublons d'impression sur un mme produit/page
// ============================================================================

// Set vid  chaque chargement de page (rechargement = impressions reset).
const dedupSet = new Set<string>();

function dedupKey(
  name: BehaviorEventName,
  props: Record<string, unknown>
): string {
  const productId = (props as { productId?: string }).productId;
  return productId
    ? `${name}:${productId}`
    : `${name}:${JSON.stringify(props)}`;
}

// ============================================================================
// ENVOI (fire-and-forget, jamais bloquant)
// ============================================================================

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('glowp_access_token');
  } catch {
    return null;
  }
}

async function send<E extends BehaviorEventName>(
  name: E,
  properties: EventProperties[E]
): Promise<void> {
  // Ddup product_impression et favorite_click sur la mme page
  if (name === 'product_impression' || name === 'favorite_click') {
    const key = dedupKey(name, properties as Record<string, unknown>);
    if (dedupSet.has(key)) return;
    dedupSet.add(key);
  }

  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  // Utiliser sendBeacon si dispo + Firefox/Safari/Chrome supportent  fiable mme  l'unload.
  // Sinon fetch keepalive.
  const body = JSON.stringify({
    name,
    page: typeof window !== 'undefined' ? window.location.pathname : null,
    session_id: getSessionId(),
    properties,
  });

  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      !token // sendBeacon ne supporte pas les headers custom (Authorization)
    ) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE_URL}/api/v1/analytics/event`, blob);
      return;
    }
    await fetch(`${API_BASE_URL}/api/v1/analytics/event`, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    });
  } catch {
    // analytics ne doit JAMAIS casser l'UX
  }
}

// ============================================================================
// API PUBLIQUE
// ============================================================================

// Batch : envoie plusieurs impressions en 1 seul POST (carrousel home,
// catalogue). -90% latence vs N appels seriels au boot.
async function sendBatch(
  events: Array<{
    name: BehaviorEventName;
    properties: Record<string, unknown>;
  }>
): Promise<void> {
  if (events.length === 0) return;
  // Dedup local sur les keys dejavu
  const filtered = events.filter(e => {
    if (e.name === 'product_impression' || e.name === 'favorite_click') {
      const key = dedupKey(e.name, e.properties);
      if (dedupSet.has(key)) return false;
      dedupSet.add(key);
    }
    return true;
  });
  if (filtered.length === 0) return;

  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const sessionId = getSessionId();
  const body = JSON.stringify({
    session_id: sessionId,
    events: filtered.map(e => ({
      name: e.name,
      page: typeof window !== 'undefined' ? window.location.pathname : null,
      session_id: sessionId,
      properties: e.properties,
    })),
  });

  try {
    await fetch(`${API_BASE_URL}/api/v1/analytics/events-batch`, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    });
  } catch {
    /* silent */
  }
}

export const behaviorTracking = {
  pageView: (props: EventProperties['page_view'] = {}) =>
    send('page_view', props),
  productImpression: (props: EventProperties['product_impression']) =>
    send('product_impression', props),
  productImpressionBatch: (
    items: Array<EventProperties['product_impression']>
  ) =>
    sendBatch(items.map(p => ({ name: 'product_impression', properties: p }))),
  productClick: (props: EventProperties['product_click']) =>
    send('product_click', props),
  favoriteClick: (props: EventProperties['favorite_click']) =>
    send('favorite_click', props),
  messageSellerClick: (props: EventProperties['message_seller_click']) =>
    send('message_seller_click', props),
  feedScrollDepth: (props: EventProperties['feed_scroll_depth']) =>
    send('feed_scroll_depth', props),
  productShared: (props: EventProperties['product_shared']) =>
    send('product_shared', props),
  shopShared: (props: EventProperties['shop_shared']) =>
    send('shop_shared', props),
  searchPerformed: (props: EventProperties['search_performed']) =>
    send('search_performed', props),
  // Funnel publication
  publicationStarted: (props: EventProperties['publication_started']) =>
    send('publication_started', props),
  publicationCompleted: (props: EventProperties['publication_completed']) =>
    send('publication_completed', props),
  publicationAbandoned: (props: EventProperties['publication_abandoned']) =>
    send('publication_abandoned', props),
  // Qualité IA — fire à la publication, comparant suggestion vs final
  aiCategoryOverridden: (props: EventProperties['ai_category_overridden']) =>
    send('ai_category_overridden', props),
  aiTargetGenderOverridden: (
    props: EventProperties['ai_target_gender_overridden']
  ) => send('ai_target_gender_overridden', props),
} as const;
